import { useState, useEffect } from 'react';
import { Event, Invite } from '../lib/types';
import { trackEvent } from '../lib/analytics';
import AttendeeListModal from './AttendeeListModal';

interface Activity {
  id: string;
  message: string;
  icon: string;
  iconColor: string;
  time: string;
  activityType: string;
  guestName: string;
  createdAt: string;
}

interface EventActivityProps {
  event: Event;
  invite?: Invite;
  isPreview?: boolean;
}

// Separate component for the download CTA with rotating features
function DownloadCtaCard({ openInApp, isPreview }: { openInApp: () => void; isPreview: boolean }) {
  const [activeFeature, setActiveFeature] = useState(0);
  
  const features = [
    {
      icon: '🔔',
      text: 'Get Updates from the Host',
      desc: 'Be the first to know if plans change',
      bgColor: 'from-amber-50 to-orange-50',
      borderColor: 'border-amber-200',
      iconBg: 'from-amber-500 to-orange-500'
    },
    {
      icon: '💬',
      text: "You're Missing Messages",
      desc: 'Guests are already chatting and sharing photos',
      bgColor: 'from-purple-50 to-pink-50',
      borderColor: 'border-purple-200',
      iconBg: 'from-pink-500 to-purple-600'
    },
    {
      icon: '📍',
      text: "Don't Forget This Event",
      desc: "Get a reminder so you don't miss the fun",
      bgColor: 'from-blue-50 to-cyan-50',
      borderColor: 'border-blue-200',
      iconBg: 'from-blue-500 to-cyan-500'
    },
  ];

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  const currentFeature = features[activeFeature];

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-100 shadow-lg">
      {/* Gradient header bar */}
      <div className="h-1.5 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500" />
      
      <div className="bg-white p-5 relative">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="inline-block relative mb-1">
            <span className="text-4xl">🎉</span>
          </div>
          <h4 className="text-xl font-black text-gray-900 leading-tight">
            You&apos;re missing{' '}
            <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
              the best part!
            </span>
          </h4>
        </div>

        {/* Rotating feature carousel - single item with different colors */}
        <div className="relative mb-4">
          <div className={`flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r ${currentFeature.bgColor} border ${currentFeature.borderColor} transition-all duration-500`}>
            <div className={`flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${currentFeature.iconBg} flex items-center justify-center text-lg shadow-md`}>
              {currentFeature.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-gray-900">{currentFeature.text}</div>
              <div className="text-xs text-gray-600">{currentFeature.desc}</div>
            </div>
          </div>
          
          {/* Carousel dots */}
          <div className="flex justify-center gap-1.5 mt-2">
            {features.map((feature, i) => (
              <button
                key={i}
                onClick={() => setActiveFeature(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === activeFeature 
                    ? `w-4 bg-gradient-to-r ${feature.iconBg}` 
                    : 'w-1.5 bg-gray-200 hover:bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Social proof - always visible above button */}
        <div className="flex items-center justify-center gap-2 mb-3 py-2 px-4 bg-gray-50 rounded-full mx-auto w-fit">
          <div className="flex -space-x-1.5">
            {[
              { emoji: '🧑', colors: 'from-blue-400 to-cyan-400' },
              { emoji: '👩', colors: 'from-pink-400 to-rose-400' },
              { emoji: '👨', colors: 'from-amber-400 to-orange-400' },
              { emoji: '👩‍🦰', colors: 'from-purple-400 to-violet-400' },
            ].map((item, i) => (
              <div 
                key={i} 
                className={`w-6 h-6 rounded-full bg-gradient-to-br ${item.colors} flex items-center justify-center text-xs border-2 border-white shadow-sm`}
              >
                {item.emoji}
              </div>
            ))}
          </div>
          <span className="text-gray-500 text-xs">
            <span className="text-gray-800 font-semibold">10M+ guests</span> are using Invitd
          </span>
        </div>

        {/* CTA Button */}
        <button
          onClick={openInApp}
          disabled={isPreview}
          className={`group relative w-full overflow-hidden rounded-xl transition-all duration-300 ${
            isPreview 
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-purple-200'
          }`}
        >
          <div className={`relative flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-bold text-white transition-all duration-300 ${
            isPreview 
              ? 'bg-gray-400' 
              : 'bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500'
          }`}>
            <span>{isPreview ? 'Preview Mode' : "Don't Miss Out"}</span>
            {!isPreview && (
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            )}
          </div>
          {!isPreview && (
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
          )}
        </button>
      </div>
    </div>
  );
}

export default function EventActivity({ event, invite, isPreview = false }: EventActivityProps) {
  const [isMounted, setIsMounted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAttendeeModalOpen, setIsAttendeeModalOpen] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalGuestCount, setTotalGuestCount] = useState(0);
  const [totalAttendees, setTotalAttendees] = useState(0);
  const [hasMoreAttendees, setHasMoreAttendees] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Fetch real activity data
  useEffect(() => {
    if (!isMounted) return;
    
    
    const fetchActivities = async () => {
      try {
        setIsLoading(true);
        // Handle both id and objectId fields
        const eventId = event.id || (event as any).objectId;
        
        if (!eventId) {
          console.error('No event ID found');
          setActivities([]);
          setIsLoading(false);
          return;
        }
        
        const response = await fetch(`/api/events/${eventId}/activities`);
        
        if (response.ok) {
          const data = await response.json();
          setActivities(data.activities || []);
        } else {
          console.error('Failed to fetch activities, status:', response.status);
          setActivities([]);
        }
      } catch (error) {
        console.error('Error fetching activities:', error);
        setActivities([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (event) {
      fetchActivities();
    }
  }, [event, isMounted]);

  // Fetch attendee counts
  useEffect(() => {
    if (!isMounted) return;
    
    
    const fetchAttendeeCounts = async () => {
      try {
        // Handle both id and objectId fields
        const eventId = event.id || (event as any).objectId;
        
        if (!eventId) {
          return;
        }
        
        const response = await fetch(`/api/events/${eventId}/attendees`);
        
        if (response.ok) {
          const data = await response.json();
          setTotalGuestCount(data.totalGuestCount || 0);
          setTotalAttendees(data.totalAttendees || 0);
          setHasMoreAttendees(data.hasMoreAttendees || false);
        }
      } catch (error) {
        console.error('Error fetching attendee counts:', error);
      }
    };

    if (event) {
      fetchAttendeeCounts();
    }
  }, [event, isMounted]);

  // Auto-rotate carousel
  useEffect(() => {
    if (!isMounted || activities.length <= 1) return;
    
    
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % activities.length);
      }, 3000); // Change every 3 seconds

      return () => clearInterval(interval);
  }, [activities.length, isMounted]);

  const openInApp = () => {
    if (isPreview) {
      console.log('Preview mode: Open in App functionality disabled');
      return;
    }

    // Track the click event
    trackEvent('join_conversation_click', {
      event_id: event.id,
      invite_id: invite?.id,
      source: 'event_activity',
      has_activities: activities.length > 0,
      total_guests: totalGuestCount,
    });

    const appUrl = invite ? 
      `https://in.invitd.us/sq6nusKPpZb?saveEvent=true&inviteID=${invite.id}` :
      `https://in.invitd.us/sq6nusKPpZb?saveEvent=true&eventID=${event.id}`;
    
    window.open(appUrl, '_blank');
  };


  // Return null during SSR to prevent hydration mismatch
  if (!isMounted) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
      {/* Enhanced Header with Pulse Animation */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            {activities.length > 0 && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              </div>
            )}
          </div>
          <div>
            <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Event Activity
            </h3>
            {activities.length > 0 && (
              <p className="text-sm text-green-600 font-medium">🔥 Live updates happening now!</p>
            )}
          </div>
        </div>
        {activities.length > 0 && (
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-green-600 font-medium">LIVE</span>
          </div>
        )}
      </div>
      
      {/* Enhanced Attendees Preview - Only show if guest list is visible */}
      {event.display_guest_list && (
        <div className="mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-900 font-bold text-lg">
                      {totalGuestCount > 0 ? (
                        <>
                          {totalGuestCount}
                          {hasMoreAttendees && '+'} Attending
                        </>
                      ) : 'No RSVPs yet'}
                    </span>
   
                  </div>
                  <p className="text-gray-600 text-sm">Join them at the event!</p>
                </div>
              </div>
              <button
                onClick={() => setIsAttendeeModalOpen(true)}
                className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-50 border border-blue-200 transition-colors duration-200 shadow-sm"
              >
                View Guests
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Activity Carousel */}
      <div className="relative overflow-hidden mb-6">
        {isLoading ? (
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-gray-200 to-blue-200 rounded-full animate-pulse"></div>
              <div className="flex-1 min-w-0">
                <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                <div className="h-3 bg-gray-200 rounded animate-pulse w-1/3"></div>
              </div>
            </div>
          </div>
        ) : activities.length === 0 ? (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border-2 border-dashed border-yellow-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-lg animate-bounce">
                🎉
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 text-sm font-bold">
                  Be the first to RSVP and start the conversation!
                </p>
                <p className="text-gray-600 text-xs">
                  Your activity will appear here and in the app
                </p>
              </div>
              <div className="text-right">
                <div className="text-xs text-orange-600 font-medium">Waiting for you!</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative">
            <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
              {activities.map((activity) => {
                // Anonymize activity message if guest list is private
                const getDisplayMessage = () => {
                  if (event.display_guest_list) {
                    return activity.message;
                  }
                  
                  // Create anonymous message based on activity type
                  switch (activity.activityType) {
                    case 'rsvpyes':
                      return 'Someone just RSVP\'d Yes!';
                    case 'rsvpno':
                      return 'Someone just RSVP\'d No';
                    case 'rsvpmaybe':
                      return 'Someone just RSVP\'d Maybe';
                    case 'message':
                      return 'Someone left a message';
                    case 'photo':
                      return 'Someone shared a photo';
                    case 'update':
                      return 'Someone updated their RSVP';
                    default:
                      return 'Someone just RSVP\'d!';
                  }
                };
                
                return (
                  <div key={activity.id} className="flex-shrink-0 w-full">
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 border border-green-200 shadow-sm">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-md ${activity.iconColor}`}>
                          {activity.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 text-sm font-semibold truncate">
                            {getDisplayMessage()}
                          </p>
                          <p className="text-gray-600 text-xs font-medium">
                            {activity.time}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Activity indicators */}
            {activities.length > 1 && (
              <div className="flex justify-center space-x-1 mt-3">
                {activities.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentIndex ? 'bg-blue-500 w-4' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Enhanced App Download CTA - Only show for invited users (not public view) */}
      {invite && (
        <DownloadCtaCard openInApp={openInApp} isPreview={isPreview} />
      )}

      {/* Attendee List Modal - Only show if guest list is visible */}
      {event.display_guest_list && (
        <AttendeeListModal
          isOpen={isAttendeeModalOpen}
          onClose={() => setIsAttendeeModalOpen(false)}
          event={event}
        />
      )}
    </div>
  );
}
